import React, { useState } from 'react';
import './App.css';

const API_BASE_URL = process.env.REACT_APP_DELETE_ACCOUNT_API || 'https://api.ale.tours';

const DeleteAccount = ({ navigateTo }) => {
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [token, setToken] = useState('');
    const [confirmed, setConfirmed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const colors = {
        primary: '#53354a',
        secondary: '#F5793B',
        accent1: '#724966',
    };

    const handleRequestCode = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/delete-account/request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            const data = await res.json();
            if (!res.ok) {
                setError(data.error || 'Something went wrong. Please try again or contact us at support@ale.tours for assistance.');
                return;
            }
            setStep(2);
        } catch {
            setError('Something went wrong. Please try again or contact us at support@ale.tours for assistance.');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCode = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/delete-account/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, code }),
            });
            const data = await res.json();
            if (!res.ok) {
                setError(data.error || 'Invalid or expired code. Please try again or contact us at support@ale.tours for assistance.');
                return;
            }
            setToken(data.token);
            setStep(3);
        } catch {
            setError('Something went wrong. Please try again or contact us at support@ale.tours for assistance.');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmDeletion = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/delete-account/confirm`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, token }),
            });
            const data = await res.json();
            if (!res.ok) {
                setError(data.error || 'Something went wrong. Please try again or contact us at support@ale.tours for assistance.');
                return;
            }
            setStep(4);
        } catch {
            setError('Something went wrong. Please try again or contact us at support@ale.tours for assistance.');
        } finally {
            setLoading(false);
        }
    };

    const stepLabels = ['Email', 'Verify', 'Confirm', 'Done'];

    const renderStepIndicator = () => (
        <div className="flex items-center justify-center mb-6">
            {stepLabels.map((label, i) => {
                const stepNum = i + 1;
                const isActive = step === stepNum;
                const isCompleted = step > stepNum;
                return (
                    <React.Fragment key={label}>
                        <div className="flex flex-col items-center">
                            <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                                style={{
                                    backgroundColor: isActive ? colors.secondary : isCompleted ? colors.primary : '#e5e7eb',
                                    color: isActive || isCompleted ? '#fff' : '#6b7280',
                                }}
                            >
                                {isCompleted ? '✓' : stepNum}
                            </div>
                            <span className="text-xs mt-1" style={{ color: isActive ? colors.secondary : '#6b7280' }}>
                                {label}
                            </span>
                        </div>
                        {i < stepLabels.length - 1 && (
                            <div
                                className="w-12 h-0.5 mx-1 mt-[-12px]"
                                style={{ backgroundColor: step > stepNum ? colors.primary : '#e5e7eb' }}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );

    const renderError = () =>
        error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-red-700 text-sm">{error}</p>
            </div>
        );

    const renderStep1 = () => (
        <form onSubmit={handleRequestCode}>
            <h2 className="text-sm font-medium mb-2" style={{ color: colors.primary }}>
                Step 1: Enter Your Email
            </h2>
            <p className="text-xs mb-4 leading-tight text-gray-600">
                Enter the email address associated with your ALE account. We will send you a verification code to confirm your identity before proceeding with account deletion.
            </p>
            {renderError()}
            <div className="mb-4">
                <label htmlFor="email" className="block text-xs font-medium text-gray-700 mb-1">
                    Email Address
                </label>
                <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
                    style={{ focusRingColor: colors.primary }}
                    disabled={loading}
                />
            </div>
            <button
                type="submit"
                disabled={loading || !email}
                className="w-full py-2 px-4 rounded-lg text-white text-sm font-bold transition-all disabled:opacity-50"
                style={{ backgroundColor: colors.primary }}
            >
                {loading ? 'Sending...' : 'Send Verification Code'}
            </button>
        </form>
    );

    const renderStep2 = () => (
        <form onSubmit={handleVerifyCode}>
            <h2 className="text-sm font-medium mb-2" style={{ color: colors.primary }}>
                Step 2: Verify Your Email
            </h2>
            <p className="text-xs mb-4 leading-tight text-gray-600">
                We've sent a 6-digit verification code to <strong>{email}</strong>. Please check your inbox and enter the code below.
            </p>
            {renderError()}
            <div className="mb-4">
                <label htmlFor="code" className="block text-xs font-medium text-gray-700 mb-1">
                    Verification Code
                </label>
                <input
                    id="code"
                    type="text"
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="123456"
                    maxLength={6}
                    pattern="[0-9]{6}"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 tracking-widest text-center text-lg"
                    disabled={loading}
                />
            </div>
            <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full py-2 px-4 rounded-lg text-white text-sm font-bold transition-all disabled:opacity-50"
                style={{ backgroundColor: colors.primary }}
            >
                {loading ? 'Verifying...' : 'Verify Code'}
            </button>
            <button
                type="button"
                onClick={() => { setStep(1); setCode(''); setError(''); }}
                className="w-full mt-2 py-2 px-4 rounded-lg text-sm text-gray-500 hover:text-gray-700 transition-all"
            >
                Use a different email
            </button>
        </form>
    );

    const renderStep3 = () => (
        <form onSubmit={handleConfirmDeletion}>
            <h2 className="text-sm font-medium mb-2" style={{ color: colors.primary }}>
                Step 3: Review & Confirm Deletion
            </h2>
            <p className="text-xs mb-3 leading-tight text-gray-600">
                Please review the following information carefully. This action is permanent and cannot be undone.
            </p>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
                <h3 className="text-xs font-medium mb-2">The following data will be permanently deleted:</h3>
                <ul className="list-disc pl-4 text-xs leading-relaxed text-gray-700">
                    <li>Account information (email, name, profile)</li>
                    <li>Usage history and app activity</li>
                    <li>Preferences and settings</li>
                    <li>AI conversation data and history</li>
                </ul>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-red-700 font-medium">
                    This action cannot be undone. Your data will be permanently deleted within 48 hours.
                </p>
            </div>

            {renderError()}

            <label className="flex items-start gap-2 mb-4 cursor-pointer">
                <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    className="mt-0.5"
                    disabled={loading}
                />
                <span className="text-xs text-gray-700">
                    I understand that all my data will be permanently deleted and this action cannot be undone.
                </span>
            </label>

            <button
                type="submit"
                disabled={loading || !confirmed}
                className="w-full py-2 px-4 rounded-lg text-white text-sm font-bold transition-all disabled:opacity-50"
                style={{ backgroundColor: '#dc2626' }}
            >
                {loading ? 'Processing...' : 'Delete My Account'}
            </button>
            <button
                type="button"
                onClick={() => navigateTo('home')}
                className="w-full mt-2 py-2 px-4 rounded-lg text-sm text-gray-500 hover:text-gray-700 transition-all"
            >
                Cancel
            </button>
        </form>
    );

    const renderStep4 = () => (
        <div className="text-center">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#dcfce7' }}>
                <span className="text-2xl" role="img" aria-label="checkmark">✓</span>
            </div>
            <h2 className="text-sm font-medium mb-2" style={{ color: colors.primary }}>
                Account Deletion Request Received
            </h2>
            <p className="text-xs mb-4 leading-tight text-gray-600">
                Your account deletion request has been received. Your data will be permanently deleted within <strong>48 hours</strong>. You will receive a confirmation email at <strong>{email}</strong> when the process is complete.
            </p>
            <p className="text-xs mb-4 text-gray-500">
                If you have any questions, please contact us at <a href="mailto:support@ale.tours" className="underline" style={{ color: colors.primary }}>support@ale.tours</a>.
            </p>
            <button
                onClick={() => navigateTo('home')}
                className="py-2 px-6 rounded-lg text-white text-sm font-bold transition-all"
                style={{ backgroundColor: colors.primary }}
            >
                Return to Home
            </button>
        </div>
    );

    const renderCurrentStep = () => {
        switch (step) {
            case 1: return renderStep1();
            case 2: return renderStep2();
            case 3: return renderStep3();
            case 4: return renderStep4();
            default: return renderStep1();
        }
    };

    return (
        <div className="container mx-auto px-4 py-4">
            <div className="max-w-5xl mx-auto bg-white p-3 rounded-lg shadow-sm">
                <h1 className="text-lg font-medium mb-1" style={{ color: colors.primary }}>Delete Your Account</h1>
                <p className="text-xs mb-4 text-gray-500">ALE — Ask. Listen. Explore.</p>

                <div className="max-w-md mx-auto">
                    {renderStepIndicator()}
                    {renderCurrentStep()}
                </div>
            </div>
        </div>
    );
};

export default DeleteAccount;
